import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Moon, Sun, RefreshCw, WifiOff, Wifi } from 'lucide-react';
import { retreatContent } from '../../data/retreatContent';

const TopHeader = ({ 
  title, 
  showBack = false, 
  darkMode, 
  onToggleDarkMode,
  onOpenSync 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Determine title based on route if not provided
  const getTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path === '/') return 'Ignatian Journey';
    if (path === '/journal') return 'My Journal';
    if (path === '/music') return 'Music for Prayer';
    if (path === '/talks') return 'Spiritual Talks';
    if (path === '/profile') return 'My Profile';
    if (path.startsWith('/day/')) {
      const dayNum = parseInt(path.split('/')[2]);
      return `Day ${dayNum}`;
    }
    if (path === '/reference/scripture') return 'Scripture Passages';
    if (path === '/reference/rimp') return 'The RIMP Way';
    if (path === '/reference/discernment') return 'Discernment Pillars';
    if (path === '/reference/examen') return 'Daily Examen';
    
    return 'Ignatian Journey';
  };

  const shouldShowBack = showBack || (location.pathname !== '/' && location.pathname !== '/journal' && location.pathname !== '/music' && location.pathname !== '/talks' && location.pathname !== '/profile');

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 no-print" data-testid="top-header">
      <div className="flex items-center justify-between h-14 px-4 max-w-4xl mx-auto">
        {/* Left: Back button or Logo */}
        <div className="flex items-center gap-3">
          {shouldShowBack ? (
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--primary)]" />
            </button>
          ) : (
            <img 
              src={retreatContent.logo}
              alt="DMU"
              className="w-8 h-8 rounded-full"
            />
          )}
          <h1 className="font-heading font-semibold text-[var(--primary)] text-lg truncate">
            {getTitle()}
          </h1>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Offline Indicator */}
          {!isOnline && (
            <div 
              className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium"
              title="You're offline. Changes will sync when online."
              data-testid="offline-indicator"
            >
              <WifiOff className="w-3 h-3" />
              <span>Offline</span>
            </div>
          )}
          {onOpenSync && (
            <button
              onClick={onOpenSync}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Sync to Cloud"
              data-testid="header-sync-btn"
            >
              <RefreshCw className="w-5 h-5 text-green-500" />
            </button>
          )}
          {onToggleDarkMode && (
            <button
              onClick={onToggleDarkMode}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
              data-testid="header-darkmode-btn"
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-amber-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
